// ============================================================================
// Avec Transformers — conversão de respostas da API em formato interno
// ============================================================================
// Campos confirmados empiricamente com dados REAIS (módulo franquia ativo).
// ============================================================================

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

// ─── UNIDADE ───────────────────────────────────────────────────────────────

/**
 * 0281 — Faturamento por formas de pagamento
 * Formato real:
 *   total: [{ valor: 94078 }]
 *   result: [{ valor: 66703.3, tipo_pagamento: 'Cartão Crédito' }, ...]
 */
export function transformFaturamento(data) {
  const result = data.result || [];
  const totalArr = Array.isArray(data.total) ? data.total : [];
  const totalValor = num(totalArr[0]?.valor);
  const formasPagamento = result.map(r => ({
    forma: r.tipo_pagamento || r.forma_pagamento || '',
    valor: num(r.valor),
  }));
  return {
    faturamento: totalValor,
    formasPagamento,
  };
}

/**
 * 0031 — Serviços realizados no período
 * Formato real:
 *   total: [{ faturamento: 72005, quantidade: '704' }]
 *   result: [{ categoria, valor_total, valor, desconto, quantidade, tipo_id }]
 */
export function transformServicosRealizados(data) {
  const result = data.result || [];
  const totalArr = Array.isArray(data.total) ? data.total : [];
  const servicosRealizados = num(totalArr[0]?.quantidade);
  const faturamentoServicos = num(totalArr[0]?.faturamento);
  // Agrupa por categoria para análise
  const porCategoria = {};
  for (const r of result) {
    const cat = r.categoria || 'Outros';
    if (!porCategoria[cat]) porCategoria[cat] = { categoria: cat, qtd: 0, faturamento: 0 };
    porCategoria[cat].qtd += num(r.quantidade);
    porCategoria[cat].faturamento += num(r.valor_total || r.valor);
  }
  return {
    servicosRealizados,
    faturamentoServicos,
    porCategoria: Object.values(porCategoria),
  };
}

/**
 * 0002 — Clientes atendidos (pelo relatório de clientes)
 * NOTA: usamos esse apenas para `clientesUnicos`; o número de VISITAS real vem do 0089
 *   (atendidos = comanda com serviço, decisão do dono em 23/10/2026; src/utils/atendidosRegua.js).
 *   O 0002 dá de 5% a 26% a menos porque a lista deixa de fora clientes ANTIGOS inteiros,
 *   com todas as vindas deles (Oscar 01-22/10: 60 de 255 clientes, 71 vindas, 296 − 71 = 225).
 *   Ele conta comanda PRODUTO/SERVICO normalmente e conta 1 visita por dia com comanda
 *   (inclusive compra de pacote sem serviço). Provável causa do corte: o filtro obrigatório
 *   como_conheceu (não confirmado).
 *
 * Formato real:
 *   total: [{ total_visitas: '248', total_clientes: '210' }]
 */
export function transformClientesAtendidos(data) {
  const totalArr = Array.isArray(data.total) ? data.total : [];
  return {
    visitas: num(totalArr[0]?.total_visitas),
    clientesUnicos: num(totalArr[0]?.total_clientes),
  };
}

/**
 * 0089 — Comandas (por situação)
 * Formato:
 *   total: [{total: 463, situacao: 'SERVICO'}, {total: 34, situacao: 'PRODUTO/SERVICO'}, {total: 1, situacao: 'PRODUTO'}]
 *
 * Regra Feel King: "visitas" / "clientes atendidos" = comandas com atendimento
 *   = SERVICO + PRODUTO/SERVICO (exclui PRODUTO puro, que é só venda de balcão)
 */
export function transformComandas(data) {
  const totalArr = Array.isArray(data.total) ? data.total : [];
  let servico = 0, produtoServico = 0, produto = 0;
  for (const t of totalArr) {
    const sit = String(t.situacao || '').toUpperCase();
    const qtd = num(t.total);
    if (sit === 'SERVICO' || sit === 'SERVIÇO') servico += qtd;
    else if (sit === 'PRODUTO/SERVICO' || sit === 'PRODUTO/SERVIÇO') produtoServico += qtd;
    else if (sit === 'PRODUTO') produto += qtd;
  }
  return {
    comandasServico: servico,
    comandasProdutoServico: produtoServico,
    comandasProduto: produto,
    totalAtendimentos: servico + produtoServico, // métrica Feel King de "visitas"
    totalComandas: servico + produtoServico + produto,
  };
}

/**
 * 0012 — Clientes novos (cadastrados no período)
 * Formato real:
 *   total: [{ total: 116 }]
 *   result: [{ id, salao_id, cliente_id, codigo, ... }]
 */
export function transformClientesNovos(data) {
  const totalArr = Array.isArray(data.total) ? data.total : [];
  return {
    clientesNovos: num(totalArr[0]?.total),
  };
}

/**
 * 0041 — Produtos vendidos
 * Formato real:
 *   total: [{ faturamento: 2523, quantidade: '25', unicas: 16 }]
 *   result: [{ profissional, cliente, data, numero, nome (produto), codigo_interno, ... }]
 */
export function transformProdutosVendidos(data) {
  const totalArr = Array.isArray(data.total) ? data.total : [];
  return {
    produtosVendidos: num(totalArr[0]?.quantidade),
    faturamentoProdutos: num(totalArr[0]?.faturamento),
    produtosUnicos: num(totalArr[0]?.unicas),
  };
}

/**
 * 0326 — Percentual de agendamentos preferenciais (por profissional)
 * Formato real confirmado:
 *   result: [{ apelido, cargo, preferencial: 70, semPreferencia: 12, percentualPreferencial: '85.37%' }, ...]
 */
export function transformAgendamentosPreferenciais(data) {
  const result = data.result || [];
  let preferenciais = 0;
  let semPreferencia = 0;
  const porProfissional = [];
  for (const r of result) {
    const p = num(r.preferencial || r.preferenciais);
    const s = num(r.semPreferencia || r.sem_preferencia);
    preferenciais += p;
    semPreferencia += s;
    porProfissional.push({
      apelido: r.apelido || '',
      cargo: r.cargo || '',
      preferencial: p,
      semPreferencia: s,
      percentual: parseFloat(String(r.percentualPreferencial || '0').replace('%', '').replace(',', '.')) || 0,
    });
  }
  const total = preferenciais + semPreferencia;
  return {
    agendamentosPreferenciais: preferenciais,
    semPreferencia,
    total,
    percentual: total > 0 ? (preferenciais / total) * 100 : 0,
    porProfissional,
  };
}

/**
 * 0007 — Taxa de retorno do estabelecimento
 * Regra Feel King: periodo1 = 60 dias, periodo2 = 30 dias (ambos mês inteiro anteriores)
 */
export function transformTaxaRetorno(data) {
  const totalArr = Array.isArray(data.total) ? data.total : [];
  const t = totalArr[0] || {};
  const result = data.result || [];
  return {
    taxaRetorno: num(t.percentual || t.taxa_retorno || 0),
    periodo1Total: num(t.total_periodo1 || result.length || 0),
    periodo2Retornaram: num(t.total_periodo2 || 0),
  };
}

/**
 * 0062 — Pacotes e pré-vendas vendidas
 * Formato real:
 *   total: [{ total: 19700 }]  (valor em R$)
 *   result: [{ data, numero, nome, item (nome pacote), quantidade, valor, desconto, total }]
 */
export function transformPacotesVendidos(data) {
  const result = data.result || [];
  const totalArr = Array.isArray(data.total) ? data.total : [];
  const valorTotalVendido = num(totalArr[0]?.total);
  const porPacote = {};
  let qtd = 0;
  for (const r of result) {
    const q = num(r.quantidade) || 1;
    qtd += q;
    const key = r.item || r.pacote || 'Outro';
    if (!porPacote[key]) porPacote[key] = { nome: key, qtd: 0, valor: 0 };
    porPacote[key].qtd += q;
    porPacote[key].valor += num(r.total || r.valor);
  }
  return {
    pacotesVendidosTotal: qtd,
    valorTotalPacotes: valorTotalVendido,
    porPacote: Object.values(porPacote),
  };
}

/**
 * 0032 — Serviços mais vendidos (extras)
 * Formato: result: [{ servico/nome, quantidade, faturamento/valor }, ...]
 */
export function transformServicosExtras(data) {
  const result = data.result || [];
  return {
    extras: result.map(r => ({
      nome: r.servico || r.nome || '',
      qtd: num(r.quantidade),
      faturamento: num(r.faturamento || r.valor),
    })),
  };
}

/**
 * 0088 — Faturamento diário do estabelecimento
 * Formato: result: [{ data, valor/faturamento }, ...]
 */
export function transformFaturamentoDiario(data) {
  const result = data.result || [];
  const porDia = {};
  for (const r of result) {
    const dia = r.data || r.dia;
    if (dia) porDia[dia] = num(r.valor || r.faturamento || r.total);
  }
  return { porDia };
}

// ─── PROFISSIONAL ──────────────────────────────────────────────────────────

/**
 * 0021 — Faturamento, comandas e ticket médio por profissional
 * Formato real:
 *   result: [{ nome, total_servicos: '166', faturamento: 29150.69, comandaQtd: 90, mediaFaturamento: 323.9 }, ...]
 */
export function transformFaturamentoProfissional(data) {
  const result = data.result || [];
  return result.map(r => {
    const faturamento = num(r.faturamento);
    const comandas = num(r.comandaQtd);
    const servicos = num(r.total_servicos);
    return {
      profissionalId: r.profissional_id || r.id || null,
      nome: r.nome || '',
      faturamento,
      comandas,
      servicos,
      ticketMedio: num(r.mediaFaturamento) || (comandas > 0 ? faturamento / comandas : 0),
      // Produtividade (ex-'Tração de Vendas') = Serviços / Comandas — regra
      // Feel King confirmada. O CAMPO segue tracaoVendas (renomear quebraria
      // todo o histórico gravado); só o rótulo na tela mudou (12/08/2026).
      tracaoVendas: comandas > 0 ? servicos / comandas : 0,
    };
  });
}

/**
 * 0024 — Serviços por profissional (extras do profissional)
 * Formato: result: [{ servico/nome, categoria, quantidade, faturamento }, ...]
 */
export function transformServicosPorProfissional(data) {
  const result = data.result || [];
  return result.map(r => ({
    servico: r.servico || r.nome || '',
    categoria: r.categoria || '',
    qtd: num(r.quantidade || r.qtd),
    faturamento: num(r.faturamento || r.valor),
    ticketMedio: num(r.mediaFaturamento || r.ticket_medio || r.valor),
  }));
}

/**
 * 0121 — Profissional e produtos vendidos
 * Formato: result: [{ profissional, produto, quantidade, valor }, ...]
 */
export function transformProdutosPorProfissional(data) {
  const result = data.result || [];
  const porProf = {};
  for (const r of result) {
    const id = r.profissional_id || r.id;
    const nome = r.profissional || r.nome || '';
    const key = id || nome;
    if (!porProf[key]) porProf[key] = { profissionalId: id, nome, qtdProdutos: 0, faturamentoProdutos: 0 };
    porProf[key].qtdProdutos += num(r.quantidade);
    porProf[key].faturamentoProdutos += num(r.faturamento || r.valor || r.total);
  }
  return Object.values(porProf);
}

/**
 * 0123 — Todas as comissões por período
 *
 * Regra Feel King para calcular a comissão REAL ganha pelo profissional:
 *   - `a_pagar`: o que ainda está pendente de pagamento
 *   - `descontos`:
 *       • NEGATIVO (ex: -200) → adiantamento já pago em dinheiro/PIX.
 *         Faz parte da comissão real ganha → SOMA o valor absoluto.
 *       • POSITIVO (ex: +500) → bônus que JÁ está incluído em `a_pagar`
 *         (a Avec já consolidou). NÃO somar de novo (evita duplicidade).
 *
 *   Fórmula:  comissao = a_pagar + (descontos < 0 ? -descontos : 0)
 *
 * Exemplo real (Victor Henrique / Moema / abril 2026):
 *   a_pagar = 9000,09  |  descontos = -200  →  comissão = 9200,09
 *
 * Formato API: result: [{ profissional, a_pagar, descontos, ... }, ...]
 */
export function transformComissoesProfissional(data) {
  const result = data.result || [];
  const porProf = {};
  for (const r of result) {
    const id = r.profissional_id || r.id;
    const nome = r.profissional || r.nome || '';
    const key = id || nome;
    if (!porProf[key]) porProf[key] = { profissionalId: id, nome, aPagar: 0, descontos: 0, comissao: 0 };
    const aPagar = num(r.a_pagar || r.apagar || r.comissao);
    const descontos = num(r.descontos || r.desconto);
    // Adiantamentos (descontos negativos) entram na comissão real;
    // bônus (descontos positivos) já estão em a_pagar — não somar.
    const lineCommission = aPagar + (descontos < 0 ? -descontos : 0);
    porProf[key].aPagar += aPagar;
    porProf[key].descontos += descontos;
    porProf[key].comissao += lineCommission;
  }
  return Object.values(porProf);
}

/**
 * 0065 — Pacotes vendidos por profissional (recepcionistas)
 * Formato: result: [{ profissional/vendedor, pacote, quantidade }, ...]
 */
export function transformPacotesPorProfissional(data) {
  const result = data.result || [];
  const porProf = {};
  for (const r of result) {
    const id = r.profissional_id || r.vendedor_id || r.id;
    const nome = r.profissional || r.vendedor || r.apelido || r.nome || '';
    const key = id || nome;
    if (!porProf[key]) porProf[key] = { profissionalId: id, nome, pacotesVendidos: 0, porPacote: {} };
    const q = num(r.quantidade) || 1;
    porProf[key].pacotesVendidos += q;
    const pkg = r.item || r.pacote || 'Outro';
    porProf[key].porPacote[pkg] = (porProf[key].porPacote[pkg] || 0) + q;
  }
  return Object.values(porProf).map(p => ({
    ...p,
    porPacote: Object.entries(p.porPacote).map(([nome, qtd]) => ({ nome, qtd })),
  }));
}

// ─── AGREGAÇÃO REDE ────────────────────────────────────────────────────────

export function aggregateNetworkFromUnits(unitsData) {
  const totals = {
    faturamento: 0,
    servicosRealizados: 0,
    visitas: 0,
    clientesUnicos: 0,
    clientesNovos: 0,
    produtosVendidos: 0,
    faturamentoProdutos: 0,
    agendamentosPreferenciais: 0,
    semPreferencia: 0,
    pacotesVendidosTotal: 0,
  };
  for (const { data } of unitsData) {
    if (!data) continue;
    for (const key of Object.keys(totals)) {
      totals[key] += num(data[key]);
    }
  }
  // Derivados (cálculo local)
  // Geração de demanda da UNIDADE = clientes novos + sem preferência
  // (definição do dono, 04/08/2026).
  totals.geracaoDemanda = totals.semPreferencia + totals.clientesNovos;
  totals.ticketMedio = totals.visitas > 0 ? totals.faturamento / totals.visitas : 0;
  totals.tracaoVendas = totals.visitas > 0 ? totals.servicosRealizados / totals.visitas : 0;
  return totals;
}
